import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { Loader2, Camera, User } from 'lucide-react';
import InputMask from 'react-input-mask';
import { useDepartments } from '@/hooks/useDepartments';
import { useSupervisors } from '@/hooks/useSupervisors';
import { Skeleton } from '@/components/ui/skeleton';

interface ProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProfileEditDialog = ({ open, onOpenChange }: ProfileEditDialogProps) => {
  const { user, profile, refreshProfile, roles } = useAuth();
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [telefone, setTelefone] = useState('');
  const [departamentoId, setDepartamentoId] = useState<string | null>(null);
  const [supervisorId, setSupervisorId] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: departments, isLoading: loadingDepts } = useDepartments();
  const { data: supervisors, isLoading: loadingSupervisors } = useSupervisors(user?.id);

  const isAdmin = roles.includes('admin') || roles.includes('ti');
  const isHR = roles.includes('rh');
  const canEditOrgFields = isAdmin || isHR;

  useEffect(() => {
    if (open && profile) {
      setDisplayName(profile.display_name || '');
      setTelefone(profile.telefone || '');
      setAvatarPreview(profile.avatar_url || null);
      setAvatarFile(null);
      setDepartamentoId(profile.departamento_id || null);
      setSupervisorId(profile.supervisor_id || null);
    }
  }, [open, profile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: 'Erro! Arquivo muito grande', description: 'O avatar deve ter no máximo 2MB.', variant: 'destructive' });
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setLoading(true);
    if (import.meta.env.DEV) console.log("[ProfileEditDialog] Iniciando processo de salvamento...");

    try {
      let newAvatarUrl = profile?.avatar_url;

      if (avatarFile) {
        if (import.meta.env.DEV) console.log("[ProfileEditDialog] Preparando arquivo para upload...");
        const filePath = `${user.id}/avatar.png`;
        const arrayBuffer = await avatarFile.arrayBuffer();

        if (import.meta.env.DEV) console.log("[ProfileEditDialog] Enviando para o storage com upsert...");
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, arrayBuffer, { 
            contentType: avatarFile.type,
            upsert: true 
          });

        if (uploadError) {
          if (import.meta.env.DEV) console.error("[ProfileEditDialog] Erro no upload:", uploadError);
          throw new Error(`Erro no upload da imagem: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
        newAvatarUrl = urlData.publicUrl;
        if (import.meta.env.DEV) console.log("[ProfileEditDialog] Avatar enviado com sucesso:", newAvatarUrl);
      }

      if (import.meta.env.DEV) console.log("[ProfileEditDialog] Atualizando metadados do usuário...");
      const { error: authError } = await supabase.auth.updateUser({
        data: { display_name: displayName }
      });
      
      if (authError) {
        if (import.meta.env.DEV) console.error("[ProfileEditDialog] Erro no Auth:", authError);
        throw authError;
      }

      if (import.meta.env.DEV) console.log("[ProfileEditDialog] Atualizando tabela usuarios_perfis...");
      const updateData: any = {
        telefone: telefone,
        avatar_url: newAvatarUrl,
        display_name: displayName || null,
        atualizado_em: new Date().toISOString()
      };

      if (canEditOrgFields) {
        updateData.departamento_id = departamentoId || null;
        updateData.supervisor_id = supervisorId || null;
      }

      const { error: profileError } = await supabase
        .from('usuarios_perfis')
        .update(updateData)
        .eq('id', user.id);
      
      if (profileError) {
        if (import.meta.env.DEV) console.error("[ProfileEditDialog] Erro no Banco de Dados:", profileError);
        throw profileError;
      }

      if (import.meta.env.DEV) console.log("[ProfileEditDialog] Sincronizando perfil...");
      await refreshProfile();
      
      toast({ 
        title: 'Pronto! Perfil atualizado', 
        description: 'Suas alterações foram salvas com sucesso.' 
      });
      
      onOpenChange(false);
    } catch (error: any) {
      if (import.meta.env.DEV) console.error("[ProfileEditDialog] Falha no salvamento:", error);
      toast({ 
        title: 'Erro! Erro ao salvar', 
        description: error.message || 'Ocorreu um erro inesperado.', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
          <DialogDescription>
            Altere suas informações básicas e foto de perfil.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          <div className="flex flex-col items-center gap-4">
            <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <Avatar className="h-24 w-24 border-2 border-muted transition-opacity group-hover:opacity-80">
                <AvatarImage src={avatarPreview || ''} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  <User className="h-12 w-12" />
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/40 rounded-full p-2">
                  <Camera className="h-6 w-6 text-white" />
                </div>
              </div>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={handleFileChange} 
              />
            </div>
            <p className="text-xs text-muted-foreground">Clique para alterar a foto (Máx 2MB)</p>
          </div>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nome de Exibição</Label>
              <Input
                id="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Como você quer ser chamado"
                disabled={loading}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Telefone</Label>
              <InputMask
                mask="(99) 9 9999-9999"
                value={telefone}
                onChange={(e: any) => setTelefone(e.target.value)}
                disabled={loading}
              >
                {(inputProps: any) => <Input {...inputProps} id="phone" placeholder="(00) 0 0000-0000" />}
              </InputMask>
            </div>

            {canEditOrgFields && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="department">Departamento</Label>
                  {loadingDepts ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={departamentoId || '__none'} onValueChange={(value) => setDepartamentoId(value === '__none' ? null : value)}>
                      <SelectTrigger id="department" disabled={loading}>
                        <SelectValue placeholder="Selecione um departamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Sem departamento</SelectItem>
                        {departments?.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.nome} {dept.sigla ? `(${dept.sigla})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="supervisor">Supervisor</Label>
                  {loadingSupervisors ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Select value={supervisorId || '__none'} onValueChange={(value) => setSupervisorId(value === '__none' ? null : value)}>
                      <SelectTrigger id="supervisor" disabled={loading}>
                        <SelectValue placeholder="Selecione um supervisor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Sem supervisor</SelectItem>
                        {supervisors?.map((sup) => (
                          <SelectItem key={sup.id} value={sup.id}>
                            {sup.display_name || sup.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};